import { useState, useCallback } from 'react'
import { t } from '@lingui/macro'
import { useSnackbar } from 'notistack'

import { useLazySearchUnassignedPatientsQuery, useAttachPatientToEstablishmentMutation } from '@/core/services/patient'
import { PatientSourceEnum } from '@/core/enums/patient-source.enum'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

export function useAttachPatientDialog(onClose: () => void) {
  const { enqueueSnackbar } = useSnackbar()
  const [searchTriggered, setSearchTriggered] = useState(false)

  const [triggerSearch, { data: searchResults, isFetching }] = useLazySearchUnassignedPatientsQuery()
  const [attachPatient, { isLoading: isAttaching }] = useAttachPatientToEstablishmentMutation()

  const user = useAppSelector(selectAuthUser)
  const establishmentId = user?.establishment?._id

  const search = useCallback(
    (email: string) => {
      if (email) {
        setSearchTriggered(true)
        triggerSearch({
          smartSearch: email,
          patientSource: PatientSourceEnum.MedconnectPatient,
        })
      }
    },
    [triggerSearch]
  )

  const attach = useCallback(
    async (patientId: string) => {
      if (!establishmentId) return

      try {
        await attachPatient({
          establishmentId,
          patientId,
        }).unwrap()
        enqueueSnackbar(t`Patient attached successfully`, { variant: 'success' })
        onClose()
      } catch (error) {
        enqueueSnackbar(t`Failed to attach patient`, { variant: 'error' })
      }
    },
    [attachPatient, enqueueSnackbar, establishmentId, onClose]
  )

  return {
    search,
    attach,
    searchResults: searchResults?.items || [],
    isFetching,
    isAttaching,
    searchTriggered,
  }
}
